import React, { Component } from "react";
import SubjectFileUpload from "../subjectFileUpload";
import ProductionFileUpload from "../productionFileUpload";
import ValidationForm from "../ValidationForm";
import ReferencesForm from "../referencesForm";
import {
  ReferenceBook,
  ReferenceTest,
  ReferenceOther,
} from "../referencesForm";
import CommentForm from "../commentForm";
import FormularHeader from "components/common/formular-header";
import { submitProblem } from "server/Exercises CRUD/Requests";
import toast from "react-hot-toast";
import {
  validateSubmissionFields,
  validateCompleteSubmission,
} from "../StudentSubmissionUtils";

interface State {
  step: number;
  maxStep: number;
  subjectFileIds: File[];
  productionFileIds: File[];
  submissionType: string;
  referenceType: string;
  comment: string;
  referenceBook: ReferenceBook | null;
  referenceOther: ReferenceOther | null;
  referenceTest: ReferenceTest | null;
  isSubmitting: boolean;
}

// Définir le type pour les props
interface Props {
  submission?: {
    subjectFileIds?: File[];
    productionFileIds?: File[];
    submissionType?: string;
    referenceType?: string;
    comment?: string;
    referenceBook?: ReferenceBook | null;
    referenceOther?: ReferenceOther | null;
    referenceTest?: ReferenceTest | null;
  };
  onCancel?: () => void;
  onSuccess?: () => void;
}

class ControlFormular extends Component<Props, State> {
  subjectFiles: File[] = [];
  productionFiles: File[] = [];

  constructor(props: Props) {
    super(props);
    this.state = {
      step: 1,
      maxStep: 4,
      subjectFileIds: props.submission?.subjectFileIds || [],
      productionFileIds: props.submission?.productionFileIds || [],
      submissionType: "control",
      referenceType: props.submission?.referenceType || "",
      comment: props.submission?.comment || "",
      referenceBook: props.submission?.referenceBook || null,
      referenceOther: props.submission?.referenceOther || null,
      referenceTest: props.submission?.referenceTest || null,
      isSubmitting: false,
    };
  }

  // revenir à l'étape précédente
  prevStep = (): void => {
    const { step } = this.state;
    if (step > 1) {
      this.setState({ step: step - 1 });
    }
  };

  // passer à l'étape suivante
  nextStep = (): void => {
    const { step } = this.state;
    const { maxStep } = this.state;

    // Validation avant de passer à l'étape suivante
    const validation = validateSubmissionFields("control", step, this.state);

    if (!validation.isValid) {
      validation.errors.forEach((error) => {
        toast.error(error);
      });
      return;
    }

    if (step < maxStep) {
      this.setState({ step: step + 1 });
    }
  };

  // Gérer le changement de champs
  handleChange =
    (input: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      this.setState({ [input]: e.target.value } as any);
    };

  // Vérifier si l'étape actuelle est valide
  isCurrentStepValid = (): boolean => {
    const validation = validateSubmissionFields(
      "control",
      this.state.step,
      this.state,
    );
    return validation.isValid;
  };

  handleSaveDraft = () => {
    console.log("Sauvegarde du brouillon a été cliqué");
    // TODO: Implement the save draft functionality
  };

  handleSubmit = async () => {
    // Validation complète avant soumission
    const validation = validateCompleteSubmission("control", this.state);

    if (!validation.isValid) {
      validation.errors.forEach((error) => {
        toast.error(error);
      });
      return;
    }

    const {
      subjectFileIds,
      productionFileIds,
      referenceType,
      submissionType,
      comment,
      referenceBook,
      referenceOther,
      referenceTest,
    } = this.state;
    const input: any = {
      subjectFileIds,
      productionFileIds,
      submissionType,
      referenceType,
      comment,
      referenceBook,
      referenceOther,
      referenceTest,
    };
    this.setState({ isSubmitting: true });

    try {
      const response = await submitProblem(input);

      if (response.status === 200) {
        // Succès - réinitialiser le formulaire
        this.setState({
          step: 1,
          maxStep: 4,
          subjectFileIds: [],
          productionFileIds: [],
          submissionType: "control",
          referenceType: "",
          comment: "",
          referenceBook: null,
          referenceOther: null,
          referenceTest: null,
          isSubmitting: false,
        });
        toast.success("Soumission réussie ! Votre contrôle a été envoyé.");
        if (this.props.onSuccess) this.props.onSuccess();
      } else {
        // Gestion des erreurs HTTP spécifiques
        this.setState({ isSubmitting: false });
        const errorMessage = this.getErrorMessage(
          response.status,
          response.body,
        );
        toast.error(errorMessage);
      }
    } catch (error) {
      // Gestion des erreurs réseau ou autres
      this.setState({ isSubmitting: false });
      console.error("Erreur lors de la soumission:", error);
      toast.error(
        "Erreur de connexion. Veuillez vérifier votre connexion internet et réessayer.",
      );
    }
  };

  // Fonction pour obtenir des messages d'erreur spécifiques selon le code de statut
  getErrorMessage = (status: number, body: any): string => {
    const errorMessage = body?.message || "Une erreur est survenue";

    switch (status) {
      case 400:
        return `Données invalides : ${errorMessage}`;
      case 401:
        return "Vous devez être connecté pour soumettre un contrôle. Veuillez vous reconnecter.";
      case 403:
        return "Vous n'avez pas l'autorisation de soumettre un contrôle.";
      case 413:
        return "Les fichiers sont trop volumineux. Veuillez réduire leur taille.";
      case 422:
        return `Erreur de validation : ${errorMessage}`;
      case 429:
        return "Trop de soumissions. Veuillez attendre quelques minutes avant de réessayer.";
      case 500:
        return "Erreur serveur. Veuillez réessayer plus tard.";
      case 502:
      case 503:
      case 504:
        return "Service temporairement indisponible. Veuillez réessayer plus tard.";
      default:
        return `Erreur ${status} : ${errorMessage}`;
    }
  };

  handleCancel = () => {
    this.setState({
      step: 1,
      maxStep: 4,
      subjectFileIds: [],
      productionFileIds: [],
      submissionType: "control",
      referenceType: "",
      comment: "",
      referenceBook: null,
      referenceOther: null,
      referenceTest: null,
      isSubmitting: false,
    });
    toast("Formulaire annulé. Retour à la liste.");
    if (this.props.onCancel) this.props.onCancel();
  };

  render() {
    const { step } = this.state;
    const { maxStep } = this.state;
    const {
      subjectFileIds,
      productionFileIds,
      submissionType,
      referenceType,
      comment,
      referenceBook,
      referenceOther,
      referenceTest,
    } = this.state;
    const values = {
      subjectFileIds,
      productionFileIds,
      submissionType,
      referenceType,
      comment,
      referenceBook,
      referenceOther,
      referenceTest,
    };
    switch (step) {
      case 1:
        return (
          <div className="flex flex-col items-center space-y-10 h-full w-full bg-white">
            <FormularHeader
              onCancel={this.handleCancel}
              onSaveDraft={this.handleSaveDraft}
              onNext={this.nextStep}
              onPrev={this.prevStep}
              isNextDisabled={!this.isCurrentStepValid()}
            />
            <h2 className="text-2xl font-semibold font-['Open Sans']">
              Contrôle et validation
            </h2>
            <ProgressBar totalSteps={4} step={this.state.step} />
            <h2 className="text-2xl text-neutral-800 text-bold font-normal font-['Open Sans']">
              Prenez une (des) photo.s lisible.s ou un (des) document.s scanné.s
              du sujet que vous avez traité.
            </h2>
            <SubjectFileUpload
              onSend={(files: File[]) => {
                this.subjectFiles = files;
                this.setState({ subjectFileIds: files });
              }}
              handleChange={this.handleChange}
              values={values}
            />
          </div>
        );
      case 2:
        return (
          <div className="flex flex-col items-center space-y-10 h-full w-full bg-white">
            <FormularHeader
              onCancel={this.handleCancel}
              onSaveDraft={this.handleSaveDraft}
              onNext={this.nextStep}
              onPrev={this.prevStep}
              isNextDisabled={!this.isCurrentStepValid()}
            />
            <h2 className="text-2xl font-semibold font-['Open Sans']">
              Contrôle et validation
            </h2>
            <ProgressBar
              totalSteps={this.state.maxStep}
              step={this.state.step}
            />
            <h2 className="text-2xl text-neutral-800 text-bold font-normal font-['Open Sans']">
              Renseignez les références de votre sujet.
            </h2>
            <ReferencesForm
              onSend={(referenceType, data) => {
                switch (referenceType) {
                  case "livre":
                    this.setState({
                      referenceType: referenceType,
                      referenceBook: data,
                    });
                    this.setState({
                      referenceTest: null,
                      referenceOther: null,
                    });
                    break;
                  case "epreuve":
                    this.setState({
                      referenceType: referenceType,
                      referenceTest: data,
                    });
                    this.setState({
                      referenceBook: null,
                      referenceOther: null,
                    });
                    break;
                  case "other":
                    this.setState({
                      referenceType: referenceType,
                      referenceOther: data,
                    });
                    this.setState({ referenceTest: null, referenceBook: null });
                    break;
                  default:
                    console.error("Type de référence invalide");
                }
              }}
              handleChange={this.handleChange}
              values={values}
            />
          </div>
        );
      case 3:
        return (
          <div className="flex flex-col items-center space-y-10 h-full w-full bg-white">
            <FormularHeader
              onCancel={this.handleCancel}
              onSaveDraft={this.handleSaveDraft}
              onNext={this.nextStep}
              onPrev={this.prevStep}
              isNextDisabled={!this.isCurrentStepValid()}
            />
            <h2 className="text-2xl font-semibold font-['Open Sans']">
              Contrôle et validation
            </h2>
            <ProgressBar
              totalSteps={this.state.maxStep}
              step={this.state.step}
            />
            <h2 className="text-1xl text-neutral-800 text-semibold font-normal font-['Open Sans']">
              Y a-t-il quelque chose que vous voudriez ajouter ?
            </h2>
            <CommentForm
              onSend={(comment: string) => {
                this.setState({ comment: comment });
              }}
              handleChange={this.handleChange}
              values={values}
            />
          </div>
        );
      case 4:
        return (
          <div className="flex flex-col items-center space-y-10 h-full w-full bg-white">
            <FormularHeader
              onCancel={this.handleCancel}
              onSaveDraft={this.handleSaveDraft}
              onNext={this.handleSubmit}
              onPrev={this.prevStep}
              isMaxStep={true}
              isSubmitting={this.state.isSubmitting}
            />
            <h2 className="text-2xl font-semibold font-['Open Sans']">
              Contrôle et validation
            </h2>
            <ProgressBar
              totalSteps={this.state.maxStep}
              step={this.state.step}
            />
            <h2 className="text-2xl text-neutral-800 text-base font-normal font-['Open Sans']">
              Prenez une (des) photo.s lisible.s ou un (des) document.s scanné.s
              de votre production (vos réponses).
            </h2>
            <ProductionFileUpload
              onSend={(files: File[]) => {
                this.productionFiles = files;
                this.setState({ productionFileIds: files });
              }}
              handleChange={this.handleChange}
              values={values}
            />
          </div>
        );
      default:
      // do nothing for now
    }
  }
}

export default ControlFormular;

interface ProgressBarProps {
  totalSteps: number;
  step: number;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ totalSteps, step }) => {
  const percentage = (step / totalSteps) * 100;

  return (
    <div className="w-full max-w-md px-4">
      <div className="flex justify-between text-sm text-gray-600 mb-2">
        <span>
          Étape {step} sur {totalSteps}
        </span>
        <span>{Math.round(percentage)}%</span>
      </div>
      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          style={{ width: `${percentage}%` }}
          className="h-full bg-gradient-to-r from-[#E3B505] to-[#F4D03F] transition-all duration-300 ease-out"
        />
      </div>
    </div>
  );
};
